<script setup>
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import XIcon from '@iconify-vue/ph/x';
import { Motion } from 'motion-v';
import {
    ComboboxAnchor,
    ComboboxContent,
    ComboboxInput,
    ComboboxItem,
    ComboboxPortal,
    ComboboxRoot,
    ComboboxTrigger,
    ComboboxViewport,
    Label,
} from 'reka-ui';
import { ErrorMessage, Field } from 'vee-validate';
import { computed, ref, watch } from 'vue';

const {
    fieldId,
    fieldName,
    label,
    legend,
    options,
    disabled = false,
    required = false,
    hasEmptyOption = false,
    filteredOptionKey,
    multipleFilteredOptionKeys,
    displayValue,
    multiple = false,
    inlineLabel = false,
    size = 'medium',
    by = null,
    withoutMargin = false,
} = defineProps({
    fieldId: {
        type: String,
        required: true,
    },
    fieldName: {
        type: String,
        required: true,
    },
    label: {
        type: String,
        required: false,
    },
    legend: {
        type: String,
        required: false,
    },
    options: {
        type: Array,
        required: true,
    },
    disabled: {
        type: Boolean,
        required: false,
    },
    required: {
        type: Boolean,
        required: false,
    },
    hasEmptyOption: {
        type: Boolean,
        required: false,
    },
    filteredOptionKey: {
        type: String,
        required: true,
    },
    multipleFilteredOptionKeys: {
        type: Array,
        required: false,
    },
    displayValue: {
        type: String,
        required: false,
    },
    multiple: {
        type: Boolean,
        required: false,
    },
    inlineLabel: {
        type: Boolean,
        required: false,
    },
    size: {
        type: String,
        required: false,
    },
    by: {
        type: String,
        required: false,
    },
    withoutMargin: {
        type: Boolean,
        required: false,
    },
});

const model = defineModel();

const isOpen = ref(false);

const query = ref('');

/**
 * Reka réaligne le texte du champ sur la valeur sélectionnée à la fermeture et
 * à la sélection : la recherche ne doit donc suivre que la saisie réelle.
 */
watch(isOpen, (open) => {
    if (!open) {
        query.value = '';
    }
});

function normalizeQuery(query) {
    return query
        .toString()
        .trim()
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .normalize('NFC')
        .toLocaleLowerCase();
}

const filteredOptions = computed(() => {
    return options.filter((option) => {
        if (multipleFilteredOptionKeys) {
            return multipleFilteredOptionKeys.some((key) => {
                return normalizeQuery(option[key]).includes(
                    normalizeQuery(query.value),
                );
            });
        }

        return normalizeQuery(option[filteredOptionKey]).includes(
            normalizeQuery(query.value),
        );
    });
});

function getInputDisplayValue(value) {
    if (multiple) {
        return '';
    }

    if (displayValue) {
        return displayValue;
    }

    return value ? value[filteredOptionKey] : '';
}

const removeSelectedOptionByIndex = (index) => {
    model.value.splice(index, 1);
};

const itemClasses = computed(() => [
    'cursor-pointer truncate duration-150 outline-none hover:bg-indigo-600 hover:text-white data-[highlighted]:bg-indigo-600 data-[highlighted]:text-white data-[state=checked]:bg-indigo-600 data-[state=checked]:text-white',
    {
        'min-h-[40px] px-5 py-2': size === 'medium',
        'h-[28px] px-3 py-1 text-sm': size === 'small',
    },
]);
</script>

<template>
    <div
        class="flex"
        :class="{
            'mb-4': !withoutMargin,
            'flex-row items-center': inlineLabel,
            'flex-col': !inlineLabel,
        }"
    >
        <Label
            v-if="label"
            :for="fieldId"
            class="whitespace-nowrap"
            :class="{
                'mr-2': inlineLabel,
                'mb-2': !inlineLabel,
                'text-sm': size === 'small',
            }"
        >
            {{ label }}
            <sup v-if="required" class="text-red-500">*</sup>
        </Label>

        <ul v-if="multiple && model" class="mb-3 flex min-h-8 flex-wrap gap-2">
            <li
                v-for="(item, index) in model"
                :key="index"
                class="flex items-center gap-2 rounded-sm px-3 py-1 last:mr-0"
                :class="{
                    'bg-gray-200': !disabled,
                    'bg-gray-100': disabled,
                }"
            >
                {{ item[filteredOptionKey] }}

                <button
                    type="button"
                    class="disabled:cursor-not-allowed"
                    :disabled="disabled"
                    @click="removeSelectedOptionByIndex(index)"
                >
                    <XIcon class="size-4" />
                </button>
            </li>
        </ul>

        <Field v-model="model" :name="fieldName">
            <div :class="{ grow: inlineLabel }">
                <ComboboxRoot
                    v-model="model"
                    v-model:open="isOpen"
                    :by="by ?? undefined"
                    :disabled="disabled"
                    :multiple="multiple"
                    ignore-filter
                >
                    <ComboboxAnchor class="flex w-full items-center rounded-sm">
                        <ComboboxInput
                            :id="fieldId"
                            class="z-10 w-full rounded-l-sm border border-r-0 border-gray-300 ring-1 duration-150 placeholder:text-gray-400 focus:border-r focus:ring-1 focus:ring-gray-300 focus:outline-hidden"
                            :class="{
                                'ring-gray-300': isOpen,
                                'ring-transparent': !isOpen,
                                'h-[50px] min-w-[220px] px-5 py-3':
                                    size === 'medium',
                                'h-[28px] min-w-[160px] px-3 py-1 text-sm':
                                    size === 'small',
                                'cursor-not-allowed bg-gray-100 focus:!ring-0':
                                    disabled,
                            }"
                            :display-value="getInputDisplayValue"
                            @input="query = $event.target.value"
                        />

                        <ComboboxTrigger
                            class="rounded-r-sm border border-gray-300 outline-1 focus:outline-gray-300"
                            :class="{
                                'outline-gray-300': isOpen,
                                'outline-transparent': !isOpen,
                                'h-[50px] px-5 py-3': size === 'medium',
                                'h-[28px] px-3 py-1': size === 'small',
                                'focus:!outline-0 disabled:cursor-not-allowed disabled:bg-gray-100':
                                    disabled,
                            }"
                        >
                            <CaretDownIcon
                                class="size-4 text-gray-500 duration-150"
                                :class="{ 'rotate-180': isOpen }"
                            />
                        </ComboboxTrigger>
                    </ComboboxAnchor>

                    <ComboboxPortal>
                        <ComboboxContent
                            position="popper"
                            :body-lock="false"
                            align="start"
                            :side-offset="4"
                            class="z-50"
                        >
                            <Motion
                                :initial="{ opacity: 0, scale: 0.96, y: -4 }"
                                :animate="{ opacity: 1, scale: 1, y: 0 }"
                                :transition="{ duration: 0.15 }"
                                class="max-h-[15rem] w-(--reka-combobox-trigger-width) overflow-x-hidden overflow-y-auto rounded-sm border border-gray-300 bg-white shadow-sm"
                            >
                                <ComboboxViewport>
                                    <ComboboxItem
                                        v-if="hasEmptyOption && !multiple"
                                        :value="null"
                                        :class="itemClasses"
                                    >
                                        &nbsp;
                                    </ComboboxItem>

                                    <ComboboxItem
                                        v-for="(
                                            option, index
                                        ) in filteredOptions"
                                        :key="index"
                                        :value="option"
                                        :class="itemClasses"
                                    >
                                        <slot
                                            name="optionContent"
                                            :option="option"
                                        />
                                    </ComboboxItem>
                                </ComboboxViewport>
                            </Motion>
                        </ComboboxContent>
                    </ComboboxPortal>
                </ComboboxRoot>
            </div>
        </Field>

        <em v-if="legend" class="mt-1 text-xs text-gray-400">
            {{ legend }}
        </em>

        <ErrorMessage :name="fieldName" class="mt-1 text-sm text-red-500" />
    </div>
</template>
